import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useMemo } from 'react';

import { DISABLED_SETTINGS_ADMINISTRATION, type SettingsAdministration } from './types';
import { useSettingsNavigation } from './use-settings-navigation';
import type { ChatSettingsSection } from '../../contexts/chat-overlay-context';
import { useUserNovaGroups } from '../../hooks/use-user-nova-groups';

export function useSettingsAdministration({
  userUuid,
  isSettingsOpen,
  isPublicMode,
  settingsSection,
}: {
  userUuid: string;
  isSettingsOpen: boolean;
  isPublicMode: boolean;
  settingsSection: ChatSettingsSection;
}): SettingsAdministration {
  const flagEnabled = useFeatureFlag('nova-skills-administration', false);
  const enabled = flagEnabled && isSettingsOpen && !isPublicMode && !!userUuid;
  const { groups: discoveredGroups, status, session, retry } = useUserNovaGroups(userUuid, enabled);
  const organizations = useMemo(
    () =>
      discoveredGroups.filter(
        (group) =>
          group.isOrganization &&
          group.parentGroupUuid === null &&
          (group.admins.includes(userUuid) || group.managers.includes(userUuid)),
      ),
    [discoveredGroups, userUuid],
  );
  const managedRoots = useMemo(
    () => organizations.filter((group) => group.managers.includes(userUuid)),
    [organizations, userUuid],
  );
  const rootAdmins = useMemo(
    () => Object.fromEntries(organizations.map((group) => [group.uuid, group.admins])),
    [organizations],
  );
  const teams = useMemo(
    () =>
      discoveredGroups
        .filter((group) => organizations.some((organization) => organization.uuid === group.parentGroupUuid))
        .sort((left, right) => left.name.localeCompare(right.name) || left.uuid.localeCompare(right.uuid)),
    [discoveredGroups, organizations],
  );
  const navigation = useSettingsNavigation({
    enabled,
    userUuid,
    session,
    settingsSection,
    status,
    showTeams: organizations.length > 0,
    showAdmins: managedRoots.length > 0,
  });

  if (!enabled) return DISABLED_SETTINGS_ADMINISTRATION;
  return {
    status,
    teams,
    organizations,
    managedRoots,
    rootAdmins,
    ...navigation,
    retry,
  };
}
